import type { ClientHomePlanHydratedNoDoc } from "api-types/homePlans/homePlanResponses";
import { ensureDefined } from "common/utils/assertOfType";
import BuilderModal from "lib/components/builders/communities/plans/modals/BuilderModal";
import useHomePlanEvents from "lib/components/builders/communities/utils/useHomePlanEvents";
import { refreshHomePlanOptions } from "lib/storage/homePlanStorage";
import type { FormEvent } from "react";
import { useState } from "react";
import Button from "ui/buttons/Button";
import useAppContext from "ui/hooks/useAppContext";
import { Message } from "ui/text/Message";
import styles from "./HomePlanActionConfirmationModal.module.scss";

interface RefreshOptionDataModalProps {
  homePlan: ClientHomePlanHydratedNoDoc;
  builderId: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  onSuccess?: (hp: ClientHomePlanHydratedNoDoc) => void;
}

export default function RefreshOptionDataModal({
  open,
  setOpen,
  onSuccess,
  homePlan,
  builderId,
}: RefreshOptionDataModalProps) {
  const { setUserNotification } = useAppContext();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const sendHomePlanEvent = useHomePlanEvents(homePlan);

  const isPinned: boolean = homePlan.optionPinState?.isPinned ?? false;

  const handleClose = () => {
    setOpen(false);
  };

  const handleRefresh = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setIsRefreshing(true);

    const [updatedHomePlan, err] = await refreshHomePlanOptions(builderId, homePlan.id);

    if (err) {
      setUserNotification(err);
      setIsRefreshing(false);
      handleClose();
    } else {
      sendHomePlanEvent("config:refreshed_homeplan_options");

      setUserNotification({
        messageType: "success",
        message: `${updatedHomePlan?.planName || "Home Plan"} option data ${isPinned ? "refreshed" : "pinned"}.`,
      });
      if (onSuccess) {
        onSuccess(ensureDefined(updatedHomePlan));
      }
      setIsRefreshing(false);
      handleClose();
    }
  };
  const title = isPinned ? "Refresh Option Data" : "Pin Option Data";
  const activeButtonText = isPinned ? "Refreshing..." : "Pinning...";
  const bodyText = isPinned
    ? `Refreshing will reset this home plan's option data and prices to the current values tied to its 
  location plan, which can result in pricing variation on links and brochures shared with homebuyers.`
    : `Pinning option data and prices will keep the current values fixed for this plan, even
  if they are changed on upstream plans in the future.`;
  return (
    <BuilderModal
      open={open}
      onClose={handleClose}
      title={title}
      variants={["footerButtons", "bodyPadding"]}
      footer={
        <>
          <Button btnSize="large" type="button" btnStyle="tertiary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            disabled={isRefreshing}
            type="submit"
            btnSize="large"
            btnStyle="primary"
            form="refresh-option-data-modal"
          >
            {isRefreshing ? activeButtonText : title}
          </Button>
        </>
      }
    >
      <form onSubmit={handleRefresh} id="refresh-option-data-modal">
        <Message messageType="warning" variant="inverted" marginBottom>
          This action will update pricing and option data.
        </Message>
        <div className={styles.modalBody}>
          <p className={styles.modalBodyText}>{bodyText}</p>
        </div>
      </form>
    </BuilderModal>
  );
}
